import React, { Component } from 'react'
import { Switch, Route, Redirect } from 'react-router-dom'

import Home from './Home'
import PasswordControl from './PasswordControl'
import Header from './Header'
import FederalRidings from './FederalRidings'
import ProvincialRidings from './ProvincialRidings'
import Riding from '../Ridings/Riding'
import PowerProjects from './PowerProjects'
import PowerProjectComponent from '../Projects/PowerProject'
import OtherProjects from './OtherProjects'
import OtherProjectComponent from '../Projects/OtherProject'

import PowerProject from '../../models/PowerProject'
import BioheatProject from '../../models/BioheatProject'
import GeothermalProject from '../../models/GeothermalProject'
import IncubatorProject from '../../models/IncubatorProject'
import ResearchProject from '../../models/ResearchProject'
import TestCenterProject from '../../models/TestCenterProject'
import RidingModel from '../../models/Riding'

import { PROVINCE_MAPPING, ACTIVE_PROVINCES, PASSWORD_HASH } from '../../constants'

import * as d3 from 'd3'

class App extends Component {
  constructor (props) {
    super(props)
    this.state = {
      rawData: {},
      rawDataLoaded: false,
      powerProjects: {},
      otherProjects: {},
      ridings: {},
      loadingComplete: false
    }
    this._ridingRender = this._ridingRender.bind(this)
    this._powerProjectRender = this._powerProjectRender.bind(this)
    this._otherProjectRender = this._otherProjectRender.bind(this)
    this._linkProjectsToRidings = this._linkProjectsToRidings.bind(this)
  }

  async componentDidMount () {
    // Load the data
    const promises = []
    promises.push(this._loadData('bc', 'data/representatives/bc.csv'))
    promises.push(this._loadData('ab', 'data/representatives/ab.csv'))
    promises.push(this._loadData('sk', 'data/representatives/sk.csv'))
    promises.push(this._loadData('on', 'data/representatives/on.csv'))
    promises.push(this._loadData('qc', 'data/representatives/qc.csv'))
    promises.push(this._loadData('fed', 'data/representatives/fed.csv'))
    promises.push(this._loadData('powerProjects', 'data/boundaries/renewable-power/renewable_power_final.csv'))
    promises.push(this._loadData('bioheatProjects', 'data/shapefiles/geocoded_bioheat_facilities.csv'))
    promises.push(this._loadData('geothermalProjects', 'data/shapefiles/geocoded_geothermal_facilities.csv'))
    promises.push(this._loadData('incubatorProjects', 'data/shapefiles/geocoded_incubators_accelerators.csv'))
    promises.push(this._loadData('researchProjects', 'data/shapefiles/geocoded_research_development.csv'))
    promises.push(this._loadData('testCenterProjects', 'data/shapefiles/geocoded_test_centres_transformed.csv'))

    const dataSetArray = await Promise.all(promises)
    const data = {}
    dataSetArray.forEach(dataSet => (data[dataSet.key] = dataSet.data))

    this.setState({ rawData: data, rawDataLoaded: true })
  }

  _loadData (key, csvPath) {
    return new Promise((resolve, reject) => {
      d3.csv(csvPath, (error, data) => {
        if (error) reject(error)
        resolve({ key, data })
      })
    })
  }

  componentDidUpdate () {
    if (this.state.rawDataLoaded && !this.state.loadingComplete) {
      const powerProjects = this._processPowerProjects()
      const otherProjects = this._processOtherProjects()
      console.log('otherProjects', otherProjects)

      const ridings = {}
      ridings.bc = this._processRidings('bc', 'British Columbia')
      ridings.ab = this._processRidings('ab', 'Alberta')
      ridings.sk = this._processRidings('sk', 'Saskatchewan')
      ridings.on = this._processRidings('on', 'Ontario')
      ridings.qc = this._processRidings('qc', 'Québec')
      ridings.fed = this._processRidings('fed', null, 'fed')
      this._linkProjectsToRidings(powerProjects, ridings)
      this._linkProjectsToRidings(otherProjects, ridings)
      this.setState({ otherProjects, powerProjects, ridings, loadingComplete: true })
    }
  }

  _processPowerProjects () {
    const powerProjects = this.state.rawData.powerProjects.map(p => {
      return new PowerProject(p)
    })
    const powerProjectMap = {}
    powerProjects.forEach(p => (powerProjectMap[p.id] = p))
    return powerProjectMap
  }

  _processOtherProjects () {
    const bioheatProjects = this.state.rawData.bioheatProjects.map(p => {
      return new BioheatProject(p)
    })
    const geothermalProjects = this.state.rawData.geothermalProjects.map(p => {
      return new GeothermalProject(p)
    })
    const incubatorProjects = this.state.rawData.incubatorProjects.map(p => {
      return new IncubatorProject(p)
    })
    const researchProjects = this.state.rawData.researchProjects.map(p => {
      return new ResearchProject(p)
    })
    const testCenterProjects = this.state.rawData.testCenterProjects.map(p => {
      return new TestCenterProject(p)
    })

    const otherProjects = bioheatProjects
      .concat(geothermalProjects)
      .concat(incubatorProjects)
      .concat(researchProjects)
      .concat(testCenterProjects)
    const otherProjectMap = {}
    otherProjects.forEach(p => (otherProjectMap[p.id] = p))
    return otherProjectMap
  }

  _processRidings (key, province, jurisdiction) {
    const ridings = this.state.rawData[key].map(r => {
      if (province) {
        r.Province = province
        r.Province_code = PROVINCE_MAPPING[province]
      }
      return new RidingModel(r, jurisdiction)
    })
    const ridingMap = {}
    ridings.forEach(r => (ridingMap[r.name] = r))
    return ridingMap
  }

  _linkProjectsToRidings (powerProjects, ridings) {
    Object.values(powerProjects).forEach(p => {
      try {
        const r = ridings.fed[p.federalRidingName()]
        r.addProject(p)
        p.addFederalRiding(r)
      } catch (e) {
        console.error('Could not match federal riding for project', p)
      }
      try {
        if (ACTIVE_PROVINCES.includes(p.provinceCode)) {
          const r = ridings[p.provinceCode][p.provincialRidingName()]
          r.addProject(p)
          p.addProvincialRiding(r)
        }
      } catch (e) {
        console.error('Could not match provincial riding for project', p)
      }
    })
  }

  _ridingRender (props) {
    const { id, level } = props.match.params
    const riding = this.state.ridings[level]
      ? Object.values(this.state.ridings[level]).filter(r => r.id === id)[0]
      : null
    return <Riding riding={riding} level={level} />
  }

  _powerProjectRender (props) {
    const { id } = props.match.params
    const project = this.state.powerProjects[id]
    return <PowerProjectComponent project={project} />
  }

  _otherProjectRender (props) {
    const { id } = props.match.params
    console.log('this.state.otherProjects', this.state.otherProjects)
    const project = this.state.otherProjects[id]
    return <OtherProjectComponent project={project} />
  }

  render () {
    return (
      <div className='App'>
        <Header />
        <main role='main' className='container-fluid'>
          <Switch>
            <Route exact path={`/`} render={() => <PasswordControl />} />
            <Route exact path={`/enter-password`} render={() => <PasswordControl />} />
            <PrivateRoute isPrivate exact path={`/home`} render={() => <Home data={this.state} />} />
            <PrivateRoute isPrivate exact path={`/federal-ridings`} render={() => <FederalRidings data={this.state} />} />
            <PrivateRoute isPrivate exact path={`/provincial-ridings`} render={() => <ProvincialRidings data={this.state} />} />
            <PrivateRoute isPrivate exact path={`/ridings/:level/:id`} render={this._ridingRender} />
            <PrivateRoute isPrivate exact path={`/power-projects`} render={() => <PowerProjects data={this.state} />} />
            <PrivateRoute isPrivate exact path={`/power-projects/:id`} render={this._powerProjectRender} />
            <PrivateRoute isPrivate exact path={`/other-projects`} render={() => <OtherProjects data={this.state} />} />
            <PrivateRoute isPrivate exact path={`/other-projects/:id`} render={this._otherProjectRender} />
          </Switch>
        </main>
      </div>
    )
  }
}

export const isLoggedIn = () => {
  return window.localStorage.getItem('password_hash') === PASSWORD_HASH
}

// Privacy test adapted from
// https://reacttraining.com/react-router/web/example/auth-workflow
const PrivateRoute = ({ component: Component, render, isPrivate, ...rest }) => {
  return (
    <Route {...rest} render={props => {
      if (!isPrivate || isLoggedIn()) {
        return render(props)
      } else {
        return <Redirect to={{ pathname: '/enter-password', state: { from: props.location } }} />
      }
    }}
    />
  )
}

export default App



// WEBPACK FOOTER //
// ./src/components/App/App.js